import axiosClient from "apis/axios-client";


// Define the interface for the API response based on your provided API structure
interface ReportManagementResponse {
  reportManagementId: number;
  requestedBy: string;
  requestedDate: string;
  answeredBy: string;
  answeredDate: string;
  requesterEmail: string;
  responderEmail: string;
  flightNumber: string;
  isWarningSent: boolean;
}

// Define the parameters interface based on the API structure
interface ReportManagementParams {
  pageNumber?: number;
  pageSize?: number;
  nameOrEmail?: string;
  requestedFromDate?: string;
  requestedToDate?: string;
  answeredFromDate?: string;
  answeredToDate?: string;
}

// Paged response shape returned by GetReportManagement.
export interface ReportManagementPagedResponse {
  totalRecords: number;
  data: ReportManagementResponse[];
}

// API function to fetch report management data
export const fetchReportManagement = async (
  params: ReportManagementParams = {}
): Promise<ReportManagementPagedResponse> => {
  try {
    const response = await axiosClient.get(
      '/AdminReportManage/GetReportManagement',
      { params }
    ) as unknown as ReportManagementPagedResponse | ReportManagementResponse[];
    // Tolerate both the new { totalRecords, data } shape and a bare array.
    if (Array.isArray(response)) {
      return { totalRecords: response.length, data: response };
    }
    return response ?? { totalRecords: 0, data: [] };
  } catch (error: any) {
    // Handle 204 No Content response (no results)
    if (error?.response?.status === 204) {
      return { totalRecords: 0, data: [] };
    }
    console.error('Error fetching report management data:', error);
    throw error;
  }
};


interface Recipient {
  reportManagementId: number;
  name: string;
  email: string;
  flightNumber: string;
}

interface SendWarningEmailRequest {
  recipients: Recipient[];
}

export const sendWarningEmails = async (request: SendWarningEmailRequest): Promise<void> => {
  await axiosClient.post('/AdminReportManage/SendWarningEmail', request);

};

interface ReportDetails {
  requesterName: string;
  requesterEmail: string;
  creditsSpent: number;
  requestDate: string;
  responderName: string;
  responderEmail: string;
  creditsReceived: number;
  responseDate: string;
  flightName: string;
  flightNumber: string;
  flightDate: string;
  departureLocation: string;
  destination: string;
  totalFlightDuration: string;
  firstClass: number;
  businessClass: number;
  economyPlusClass: number;
  economyClass: number;
  nonRevListed: number;
  nonFirst: number;
  nonBusiness: number;
  nonEconomyPlus: number;
  nonEconomy: number;
  moreFirstClass: boolean;
  moreBusinessClass: boolean;
  moreEcoClass: boolean;
  moreEcoPlus: boolean;
  comments: string;
}


export const getReportManagementDetails = async (reportId: number): Promise<ReportDetails> => {
  const response = await axiosClient.get(`/AdminReportManage/GetReportManagementDetails/${reportId}`) as unknown as ReportDetails;
  return response; // axiosClient interceptor returns response.data directly
};

export interface FeedbackItem {
  id: number;
  name: string;
  feedbackDate: string;
  email: string;
}

export interface FeedbackPagedResponse {
  totalRecords: number;
  data: FeedbackItem[];
}

export const getFeedbacks = async (params: {
  pageNumber: number;
  pageSize: number;
  nameOrEmail?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<FeedbackPagedResponse> => {
  try {
    const response = await axiosClient.get('/AdminReportManage/GetFeedbacks', { params }) as unknown as
      | FeedbackPagedResponse
      | FeedbackItem[];
    // Tolerate both the new { totalRecords, data } shape and a bare array.
    if (Array.isArray(response)) {
      return { totalRecords: response.length, data: response };
    }
    return response ?? { totalRecords: 0, data: [] };
  } catch (error: any) {
    if (error?.response?.status === 204) {
      return { totalRecords: 0, data: [] };
    }
    throw error;
  }
};

export interface FeedbackDetails {
  username: string;
  feedbackComment: string;
  feedbackTypeName: string;
  imageUrl: string | null;
}

export const getFeedbackById = async (feedbackId: number): Promise<FeedbackDetails> => {
  const response = await axiosClient.get('/AdminReportManage/GetFeedbackById', {
    params: { feedbackId },
  }) as unknown as FeedbackDetails;
  return response; // axiosClient interceptor returns response.data directly
};